import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Metadata, CompositeKey, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import {
  IzzyOrganizationActionEntity,
  OrganizationActionEntityExtendedClient,
  IzzyIzzyActionEntity,
  IzzyIzzyActionCategoryEntity,
  IzzyMetadataEngine,
  ChannelActionConfiguration,
  IzzyChannelActionEntity,
  ChannelEntityExtended
} from 'mj_generatedentities';
import { BCSaaSPlanEntity, BCSaaSOrganizationCredentialEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { MJActionEntity, MJAIAgentEntity, MJCredentialEntity } from '@memberjunction/core-entities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';
import { DisplayCredentialName } from '../../utils/org-credentials';

/**
 * Action card for gallery display
 */
interface GalleryActionCard {
  izzyAction: IzzyIzzyActionEntity;
  category: IzzyIzzyActionCategoryEntity | null;
  planName: string;
  isConfigured: boolean;
}

/**
 * Channel override for an action - represents a ChannelAction that overrides org-level settings
 */
interface ChannelOverride {
  /** The channel entity */
  channel: ChannelEntityExtended;
  /** The ChannelAction entity (if exists) */
  channelAction: IzzyChannelActionEntity | null;
  /** Current status at channel level */
  status: 'Active' | 'Disabled' | 'Pending' | 'Revoked' | 'Inherited';
  /** Whether this channel has an explicit override (vs inheriting from org) */
  hasOverride: boolean;
  /** Credential ID at channel level (null means inherit from org) */
  credentialId: string | null;
  /** Agent scope mode at channel level */
  agentScopeMode: 'global' | 'specific' | 'inherited';
  /** Selected agent IDs when mode is 'specific' */
  selectedAgentIds: string[];
}

/**
 * Edit form for channel override
 */
interface ChannelOverrideEditForm {
  status: 'Active' | 'Disabled';
  credentialId: string | null;
  agentScopeMode: 'global' | 'specific';
  selectedAgentIds: string[];
}

/**
 * Display wrapper for OrganizationAction with UI state
 */
interface OrganizationActionDisplay {
  orgAction: IzzyOrganizationActionEntity;
  izzyAction: IzzyIzzyActionEntity | null;
  mjAction: MJActionEntity | null;
  isEditing: boolean;
  editForm: OrganizationActionEditForm;
  /** Channel overrides for this action */
  channelOverrides: ChannelOverride[];
  /** Whether the channel overrides section is expanded */
  channelOverridesExpanded: boolean;
  /** Channel ID currently being edited (null if none) */
  editingChannelId: string | null;
  /** Edit form for channel override */
  channelOverrideEditForm: ChannelOverrideEditForm;
}

/**
 * Sub-agent display for the UI
 */
interface SubAgentDisplay {
  /** Agent ID (GUID) */
  id: string;
  /** Full agent name (e.g., "Izzy - Knowledge Gatherer") */
  fullName: string;
  /** Display name (e.g., "Knowledge Gatherer") - stripped prefix */
  displayName: string;
  /** Font Awesome icon class */
  iconClass: string;
}

/**
 * Edit form for OrganizationAction
 */
interface OrganizationActionEditForm {
  status: 'Active' | 'Disabled';
  sequence: number;
  credentialId: string | null;
  configuration: string;
  /** Agent scope mode: 'global' for all agents, 'specific' for selected agents */
  agentScopeMode: 'global' | 'specific';
  /** Selected agent IDs when mode is 'specific' */
  selectedAgentIds: string[];
}

/**
 * Component state for persistence
 */
interface OrganizationActionsState {
  selectedOrganizationId?: string;
}

/**
 * Organization Actions Configuration Component
 *
 * Manages organization-level action configuration with:
 * - Organization selection
 * - List of configured OrganizationActions
 * - Add actions from gallery
 * - Edit action settings (status, sequence, credentials)
 * - Remove actions
 * - Sequence reordering via drag or buttons
 */
@Component({
  standalone: false,
  selector: 'izzy-organization-actions',
  templateUrl: './organization-actions.component.html',
  styleUrls: ['./organization-actions.component.css']
})
export class OrganizationActionsComponent implements OnInit, OnDestroy, OnChanges, IAdminPanel {
  /** Credential name without BCSaaS's ` [organizationID]` suffix (template helper). */
  protected readonly displayCredentialName = DisplayCredentialName;


  // ========== Inputs/Outputs ==========
  @Input() initialState: OrganizationActionsState | null = null;
  @Input() organizationId: string | undefined;
  @Output() stateChange = new EventEmitter<OrganizationActionsState>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() openGallery = new EventEmitter<{ organizationId: string }>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordPKey: CompositeKey }>();

  constructor() {}

  // ========== Data ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected orgActions: OrganizationActionDisplay[] = [];
  protected availableIzzyActions: IzzyIzzyActionEntity[] = [];
  protected availableMjActions: MJActionEntity[] = [];
  protected credentials: MJCredentialEntity[] = [];
  /** Channels for the selected organization */
  protected orgChannels: ChannelEntityExtended[] = [];

  // ========== Selection State ==========
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;

  // ========== UI State ==========
  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';

  // ========== Create Mode ==========
  protected isCreating: boolean = false;
  protected createForm: OrganizationActionEditForm = this.getEmptyForm();
  protected selectedIzzyActionId: string = '';

  // ========== Confirm Dialog ==========
  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  // ========== Gallery Modal ==========
  protected galleryModalVisible: boolean = false;
  protected gallerySearchTerm: string = '';
  protected galleryCategories: IzzyIzzyActionCategoryEntity[] = [];
  protected gallerySelectedCategoryId: string | null = null;
  protected galleryActionCards: GalleryActionCard[] = [];
  protected galleryPlans: BCSaaSPlanEntity[] = [];
  protected isAddingAction: boolean = false;

  // ========== Agent Scope ==========
  /** Available sub-agents for agent scope selection */
  protected availableSubAgents: SubAgentDisplay[] = [];
  /** The parent Izzy Single Message agent ID */
  private izzySingleMessageAgentId: string | null = null;
  /** Name of the main Izzy agent */
  private readonly IZZY_MAIN_AGENT_NAME = 'Izzy - Single Message';
  /** Prefix to strip from sub-agent names for display */
  private readonly AGENT_NAME_PREFIX = 'Izzy - ';

  // ========== Cleanup ==========
  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();

    // Apply initial state
    if (this.organizationId) {
      const org = this.organizations.find(o => o.ID === this.organizationId);
      if (org) {
        this.selectOrganization(org);
      }
    } else if (this.initialState?.selectedOrganizationId) {
      const org = this.organizations.find(o => o.ID === this.initialState!.selectedOrganizationId);
      if (org) {
        this.selectOrganization(org);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to organizationId input changes
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      const newOrgId = changes['organizationId'].currentValue;
      if (newOrgId && this.organizations.length > 0) {
        const org = this.organizations.find(o => o.ID === newOrgId);
        if (org && org.ID !== this.selectedOrganization?.ID) {
          this.selectOrganization(org);
        }
      }
    }
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      await IzzyMetadataEngine.Instance.Config(false);

      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
      this.availableIzzyActions = IzzyMetadataEngine.Instance.IzzyActions.filter(a => a.Status === 'Active');
      this.galleryCategories = IzzyMetadataEngine.Instance.IzzyActionCategories;
      this.galleryPlans = IzzyMetadataEngine.Instance.Plans;

      // Load MJ Actions and sub-agents in parallel
      const rv = new RunView();
      const [actionsResult] = await Promise.all([
        rv.RunView<MJActionEntity>({
          EntityName: 'MJ: Actions',
          ExtraFilter: "Status = 'Active'"
        }),
        this.loadAvailableSubAgents()
      ]);
      if (actionsResult.Success) {
        this.availableMjActions = actionsResult.Results;
      }

      if (!this.selectedOrganization && this.organizations.length > 0) {
        this.selectOrganization(this.organizations[0]);
      }

      // Emit default status (org actions don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading organization actions data:', error);
      this.errorMessage = 'Failed to load data. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCredentials(): Promise<void> {
    if (!this.selectedOrganization) {
      this.credentials = [];
      return;
    }

    try {
      const rv = new RunView();

      // Load org-credential bridge records for this org
      const linksResult = await rv.RunView<BCSaaSOrganizationCredentialEntity>({
        EntityName: 'BC: Organization Credentials',
        ExtraFilter: `OrganizationID = '${this.selectedOrganization.ID}' AND Status = 'Active'`,
        ResultType: 'entity_object'
      });
      const credentialIds = (linksResult.Success ? linksResult.Results : []).map(l => l.CredentialID);

      if (credentialIds.length === 0) {
        this.credentials = [];
        return;
      }

      // Load the actual credential entities
      const idList = credentialIds.map(id => `'${id}'`).join(',');
      const result = await rv.RunView<MJCredentialEntity>({
        EntityName: 'MJ: Credentials',
        ExtraFilter: `ID IN (${idList}) AND IsActive = 1`,
        OrderBy: 'Name ASC'
      });
      this.credentials = result.Success ? result.Results : [];
    } catch (error) {
      console.error('Error loading credentials:', error);
    }
  }

  private async loadOrganizationActions(): Promise<void> {
    if (!this.selectedOrganization) {
      this.orgActions = [];
      this.orgChannels = [];
      return;
    }

    try {
      // Load channels for this organization
      this.orgChannels = IzzyMetadataEngine.Instance.Channels.filter(
        c => c.OrganizationID === this.selectedOrganization!.ID && c.Status === 'Active'
      );

      // Get all org actions (including disabled for re-enable logic)
      const allActions = IzzyMetadataEngine.Instance.GetOrganizationActions(this.selectedOrganization.ID);

      // Only display Active actions in the UI
      const activeActions = allActions.filter(oa => oa.Status === 'Active');

      this.orgActions = activeActions.map(oa => {
        const izzyAction = this.availableIzzyActions.find(ia => ia.ActionID === oa.ActionID) || null;
        const mjAction = this.availableMjActions.find(a => a.ID === oa.ActionID) || null;

        // Build channel overrides for this action
        const channelOverrides = this.buildChannelOverrides(oa.ActionID);

        return {
          orgAction: oa,
          izzyAction,
          mjAction,
          isEditing: false,
          editForm: this.orgActionToForm(oa),
          channelOverrides,
          channelOverridesExpanded: false,
          editingChannelId: null,
          channelOverrideEditForm: this.getEmptyChannelOverrideForm()
        };
      });

      this.orgActions.sort((a, b) => a.orgAction.Sequence - b.orgAction.Sequence);
    } catch (error) {
      console.error('Error loading organization actions:', error);
      this.errorMessage = 'Failed to load organization actions.';
    }
  }

  /**
   * Build channel overrides for a specific action.
   * Returns array of ChannelOverride for each channel that has an explicit override.
   */
  private buildChannelOverrides(actionId: string): ChannelOverride[] {
    const overrides: ChannelOverride[] = [];

    for (const channel of this.orgChannels) {
      // Check if this channel has an explicit override for this action
      const channelAction = channel.ChannelActions.find(ca => ca.ActionID === actionId);

      if (channelAction) {
        // Parse agent scope from configuration
        const scope = this.parseAgentScopeFromConfig(channelAction.Configuration);

        overrides.push({
          channel,
          channelAction,
          status: channelAction.Status,
          hasOverride: true,
          credentialId: channelAction.CredentialID,
          agentScopeMode: scope.mode,
          selectedAgentIds: scope.agentIds
        });
      }
    }

    return overrides;
  }

  /**
   * Parse agent scope from configuration JSON
   */
  private parseAgentScopeFromConfig(configuration: string | null): { mode: 'global' | 'specific'; agentIds: string[] } {
    if (!configuration) {
      return { mode: 'global', agentIds: [] };
    }

    try {
      const config: ChannelActionConfiguration = JSON.parse(configuration);
      if (config.agentScope && config.agentScope.mode === 'specific' && Array.isArray(config.agentScope.agentIds)) {
        return {
          mode: 'specific',
          agentIds: config.agentScope.agentIds
        };
      }
    } catch {
      // Invalid JSON, use default
    }

    return { mode: 'global', agentIds: [] };
  }

  /**
   * Get empty form for channel override editing
   */
  private getEmptyChannelOverrideForm(): ChannelOverrideEditForm {
    return {
      status: 'Active',
      credentialId: null,
      agentScopeMode: 'global',
      selectedAgentIds: []
    };
  }

  // ========== Organization Selection ==========

  selectOrganization(org: mjBizAppsCommonOrganizationEntity): void {
    this.selectedOrganization = org;
    this.cancelCreate();
    this.loadOrganizationActions();
    this.loadCredentials();
    this.emitStateChange();
  }

  onOrganizationChange(orgId: string): void {
    const org = this.organizations.find(o => o.ID === orgId);
    if (org) {
      this.selectOrganization(org);
    }
  }

  // ========== Create Mode ==========

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
    this.selectedIzzyActionId = '';
    this.cancelAllEdits();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
    this.selectedIzzyActionId = '';
  }

  onIzzyActionChange(izzyActionId: string): void {
    this.selectedIzzyActionId = izzyActionId;
  }

  get availableActionsForCreate(): IzzyIzzyActionEntity[] {
    const configuredActionIds = new Set(this.orgActions.map(oa => oa.orgAction.ActionID));
    return this.availableIzzyActions.filter(ia => !configuredActionIds.has(ia.ActionID));
  }

  async saveCreate(): Promise<void> {
    if (!this.selectedOrganization || !this.selectedIzzyActionId) {
      this.errorMessage = 'Please select an action.';
      return;
    }

    const izzyAction = this.availableIzzyActions.find(ia => ia.ID === this.selectedIzzyActionId);
    if (!izzyAction) {
      this.errorMessage = 'Invalid action selected.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      const md = new Metadata();
      const newAction = await md.GetEntityObject<IzzyOrganizationActionEntity>('Organization Actions');

      newAction.OrganizationID = this.selectedOrganization.ID;
      newAction.ActionID = izzyAction.ActionID;
      newAction.Status = this.createForm.status;
      newAction.Sequence = this.createForm.sequence;
      newAction.Configuration = this.buildConfigurationFromForm(this.createForm) || null;
      newAction.CredentialID = this.createForm.credentialId || null;

      const saveResult = await newAction.Save();
      if (!saveResult) {
        throw new Error('Failed to save organization action');
      }

      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadOrganizationActions();

      this.showSuccess('Organization action created');
      this.cancelCreate();
    } catch (error) {
      console.error('Error creating organization action:', error);
      this.errorMessage = 'Failed to create organization action. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Edit Mode ==========

  startEdit(display: OrganizationActionDisplay): void {
    this.cancelAllEdits();
    this.cancelCreate();
    display.isEditing = true;
    display.editForm = this.orgActionToForm(display.orgAction);
  }

  cancelEdit(display: OrganizationActionDisplay): void {
    display.isEditing = false;
    display.editForm = this.orgActionToForm(display.orgAction);
  }

  cancelAllEdits(): void {
    for (const display of this.orgActions) {
      display.isEditing = false;
      display.editForm = this.orgActionToForm(display.orgAction);
    }
  }

  async saveEdit(display: OrganizationActionDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      const action = display.orgAction;

      action.Status = display.editForm.status;
      action.Sequence = display.editForm.sequence;
      action.Configuration = this.buildConfigurationFromForm(display.editForm) || null;
      action.CredentialID = display.editForm.credentialId || null;

      const saveResult = await action.Save();
      if (!saveResult) {
        throw new Error('Failed to save organization action');
      }

      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadOrganizationActions();

      this.showSuccess('Organization action updated');
    } catch (error) {
      console.error('Error updating organization action:', error);
      this.errorMessage = 'Failed to update organization action. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Delete (Soft Delete) ==========

  confirmDelete(display: OrganizationActionDisplay): void {
    const actionName = display.izzyAction?.Name || display.mjAction?.Name || 'Unknown Action';
    this.showConfirmDialog(
      'Remove Organization Action',
      `Are you sure you want to remove "${actionName}" from this organization? This will also remove it from all channels using inheritance.`,
      async () => {
        await this.disableAction(display);
      }
    );
  }

  /**
   * Soft delete: Set status to Disabled instead of actually deleting.
   * This preserves audit trail and allows re-enabling later.
   */
  private async disableAction(display: OrganizationActionDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      (display.orgAction as OrganizationActionEntityExtendedClient).Disable();
      const result = await display.orgAction.Save();
      if (!result) {
        const errorDetail = display.orgAction.LatestResult?.Message || 'Unknown error';
        throw new Error(`Failed to disable organization action: ${errorDetail}`);
      }

      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadOrganizationActions();

      this.showSuccess('Organization action removed');
    } catch (error) {
      console.error('Error disabling organization action:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      this.errorMessage = `Failed to remove organization action: ${errorMsg}`;
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Sequence Reordering ==========

  async moveUp(display: OrganizationActionDisplay): Promise<void> {
    const index = this.orgActions.indexOf(display);
    if (index <= 0) return;

    await this.swapSequence(display, this.orgActions[index - 1]);
  }

  async moveDown(display: OrganizationActionDisplay): Promise<void> {
    const index = this.orgActions.indexOf(display);
    if (index < 0 || index >= this.orgActions.length - 1) return;

    await this.swapSequence(display, this.orgActions[index + 1]);
  }

  private async swapSequence(a: OrganizationActionDisplay, b: OrganizationActionDisplay): Promise<void> {
    this.isSaving = true;

    try {
      const tempSeq = a.orgAction.Sequence;
      a.orgAction.Sequence = b.orgAction.Sequence;
      b.orgAction.Sequence = tempSeq;

      await a.orgAction.Save();
      await b.orgAction.Save();

      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadOrganizationActions();
    } catch (error) {
      console.error('Error reordering actions:', error);
      this.errorMessage = 'Failed to reorder actions.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Helpers ==========

  private getEmptyForm(): OrganizationActionEditForm {
    return {
      status: 'Active',
      sequence: this.orgActions.length > 0
        ? Math.max(...this.orgActions.map(oa => oa.orgAction.Sequence)) + 1
        : 0,
      credentialId: null,
      configuration: '',
      agentScopeMode: 'global',
      selectedAgentIds: []
    };
  }

  private orgActionToForm(action: IzzyOrganizationActionEntity): OrganizationActionEditForm {
    // Parse agent scope from configuration
    let agentScopeMode: 'global' | 'specific' = 'global';
    let selectedAgentIds: string[] = [];

    if (action.Configuration) {
      try {
        const config: ChannelActionConfiguration = JSON.parse(action.Configuration);
        if (config.agentScope) {
          agentScopeMode = config.agentScope.mode || 'global';
          selectedAgentIds = config.agentScope.agentIds || [];
        }
      } catch {
        // Invalid JSON, use defaults
      }
    }

    return {
      status: action.Status as 'Active' | 'Disabled',
      sequence: action.Sequence,
      credentialId: action.CredentialID || null,
      configuration: action.Configuration || '',
      agentScopeMode,
      selectedAgentIds
    };
  }

  /**
   * Build the Configuration JSON from the form, merging agent scope with any existing config
   */
  private buildConfigurationFromForm(form: OrganizationActionEditForm): string {
    let config: ChannelActionConfiguration = {};

    // Try to preserve any existing actionConfig
    if (form.configuration) {
      try {
        const existingConfig: ChannelActionConfiguration = JSON.parse(form.configuration);
        config = { ...existingConfig };
      } catch {
        // Invalid JSON, start fresh
      }
    }

    // Set agent scope based on form
    if (form.agentScopeMode === 'specific' && form.selectedAgentIds.length > 0) {
      config.agentScope = {
        mode: 'specific',
        agentIds: form.selectedAgentIds
      };
    } else {
      // Remove agentScope if global (to keep config clean)
      delete config.agentScope;
    }

    // Return empty string if config is empty, otherwise JSON string
    if (Object.keys(config).length === 0) {
      return '';
    }
    return JSON.stringify(config);
  }

  /**
   * Load available sub-agents for agent scope selection.
   */
  private async loadAvailableSubAgents(): Promise<void> {
    try {
      const rv = new RunView();

      // First, find the main Izzy Single Message agent
      const mainAgentResult = await rv.RunView<MJAIAgentEntity>({
        EntityName: 'MJ: AI Agents',
        ExtraFilter: `Name = '${this.IZZY_MAIN_AGENT_NAME}'`
      });

      if (!mainAgentResult.Success || mainAgentResult.Results.length === 0) {
        console.warn('Could not find Izzy - Single Message agent');
        return;
      }

      const mainAgent = mainAgentResult.Results[0];
      this.izzySingleMessageAgentId = mainAgent.ID;

      // Add the main agent first (displayed as "Izzy")
      this.availableSubAgents = [{
        id: mainAgent.ID,
        fullName: mainAgent.Name,
        displayName: 'Izzy (Orchestrator)',
        iconClass: mainAgent.IconClass || 'fa-solid fa-robot'
      }];

      // Now load all sub-agents (children of the main agent)
      const subAgentsResult = await rv.RunView<MJAIAgentEntity>({
        EntityName: 'MJ: AI Agents',
        ExtraFilter: `ParentID = '${mainAgent.ID}' AND Status = 'Active'`,
        OrderBy: 'ExecutionOrder'
      });

      if (subAgentsResult.Success) {
        for (const subAgent of subAgentsResult.Results) {
          // Strip the "Izzy - " prefix for display
          let displayName = subAgent.Name;
          if (displayName.startsWith(this.AGENT_NAME_PREFIX)) {
            displayName = displayName.substring(this.AGENT_NAME_PREFIX.length);
          }

          this.availableSubAgents.push({
            id: subAgent.ID,
            fullName: subAgent.Name,
            displayName,
            iconClass: subAgent.IconClass || 'fa-solid fa-cog'
          });
        }
      }
    } catch (error) {
      console.error('Error loading sub-agents:', error);
    }
  }

  /**
   * Toggle agent selection in edit form
   */
  protected toggleAgentSelection(form: OrganizationActionEditForm, agentId: string): void {
    const index = form.selectedAgentIds.indexOf(agentId);
    if (index === -1) {
      form.selectedAgentIds.push(agentId);
    } else {
      form.selectedAgentIds.splice(index, 1);
    }
  }

  /**
   * Check if an agent is selected in the form
   */
  protected isAgentSelected(form: OrganizationActionEditForm, agentId: string): boolean {
    return form.selectedAgentIds.includes(agentId);
  }

  /**
   * Get display text for agent scope
   */
  protected getAgentScopeDisplayText(configuration: string | null): string {
    if (!configuration) {
      return 'All Agents';
    }

    try {
      const config: ChannelActionConfiguration = JSON.parse(configuration);
      if (!config.agentScope || config.agentScope.mode === 'global') {
        return 'All Agents';
      }

      const agentIds = config.agentScope.agentIds || [];
      if (agentIds.length === 0) {
        return 'All Agents';
      }

      // Look up display names from availableSubAgents
      const displayNames = agentIds.map(id => {
        const agent = this.availableSubAgents.find(a => a.id === id);
        return agent?.displayName || 'Unknown';
      });

      if (displayNames.length === 1) {
        return displayNames[0];
      }
      if (displayNames.length <= 2) {
        return displayNames.join(' & ');
      }
      return `${displayNames[0]} + ${displayNames.length - 1} more`;
    } catch {
      return 'All Agents';
    }
  }

  getCredentialName(credentialId: string | null): string {
    if (!credentialId) return 'None';
    const cred = this.credentials.find(c => c.ID === credentialId);
    return cred ? cred.Name : 'Unknown';
  }

  getActionName(display: OrganizationActionDisplay): string {
    return display.izzyAction?.Name || display.mjAction?.Name || 'Unknown Action';
  }

  getActionDescription(display: OrganizationActionDisplay): string {
    return display.izzyAction?.Description || display.mjAction?.Description || '';
  }

  getActionIconClass(display: OrganizationActionDisplay): string {
    const iconClass = display.izzyAction?.IconClass;
    if (!iconClass) {
      return 'fa fa-cog';
    }
    // If it already has 'fa ' or 'fa-solid' prefix, use as-is
    if (iconClass.startsWith('fa ') || iconClass.startsWith('fa-solid') || iconClass.startsWith('fas ')) {
      return iconClass;
    }
    // Otherwise, add the 'fa ' prefix for Font Awesome 4/5 style icons
    return `fa ${iconClass}`;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Disabled': return 'status-disabled';
      default: return '';
    }
  }

  hasCredentialsConfigured(display: OrganizationActionDisplay): boolean {
    return !!(display.orgAction.CredentialID);
  }

  openActionGallery(): void {
    if (this.selectedOrganization) {
      this.buildGalleryActionCards();
      this.gallerySearchTerm = '';
      this.gallerySelectedCategoryId = null;
      this.galleryModalVisible = true;
    }
  }

  closeGalleryModal(): void {
    this.galleryModalVisible = false;
    this.gallerySearchTerm = '';
    this.gallerySelectedCategoryId = null;
  }

  private buildGalleryActionCards(): void {
    // Only consider ACTIVE actions as "configured" - disabled ones should be available to re-enable
    const activeActionIds = new Set(this.orgActions.map(oa => oa.orgAction.ActionID));

    this.galleryActionCards = this.availableIzzyActions.map(action => {
      const category = this.galleryCategories.find(c => c.ID === action.CategoryID) || null;
      const plan = this.galleryPlans.find(p => p.Level === action.MinimumPlanLevel);

      return {
        izzyAction: action,
        category,
        planName: plan?.Name || 'Free',
        isConfigured: activeActionIds.has(action.ActionID)
      };
    });
  }

  get filteredGalleryActions(): GalleryActionCard[] {
    let result = this.galleryActionCards;

    // Filter by category
    if (this.gallerySelectedCategoryId) {
      const categoryIds = this.getDescendantCategoryIds(this.gallerySelectedCategoryId);
      result = result.filter(card => card.category && categoryIds.includes(card.category.ID));
    }

    // Filter by search term
    if (this.gallerySearchTerm.trim()) {
      const term = this.gallerySearchTerm.toLowerCase();
      result = result.filter(card =>
        card.izzyAction.Name.toLowerCase().includes(term) ||
        (card.izzyAction.Description?.toLowerCase().includes(term))
      );
    }

    // Hide already configured actions
    result = result.filter(card => !card.isConfigured);

    return result;
  }

  private getDescendantCategoryIds(categoryId: string): string[] {
    const ids = [categoryId];
    const children = this.galleryCategories.filter(c => c.ParentID === categoryId);
    for (const child of children) {
      ids.push(...this.getDescendantCategoryIds(child.ID));
    }
    return ids;
  }

  get rootCategories(): IzzyIzzyActionCategoryEntity[] {
    return this.galleryCategories.filter(c => !c.ParentID);
  }

  getCategoryChildren(parentId: string): IzzyIzzyActionCategoryEntity[] {
    return this.galleryCategories.filter(c => c.ParentID === parentId);
  }

  selectGalleryCategory(categoryId: string | null): void {
    this.gallerySelectedCategoryId = categoryId;
  }

  async addActionFromGallery(card: GalleryActionCard): Promise<void> {
    if (!this.selectedOrganization || card.isConfigured) return;

    this.isAddingAction = true;

    try {
      // Check if a disabled OrganizationAction already exists for this action
      const allOrgActions = IzzyMetadataEngine.Instance.GetOrganizationActions(this.selectedOrganization.ID);
      const existingDisabled = allOrgActions.find(
        oa => oa.ActionID === card.izzyAction.ActionID && oa.Status === 'Disabled'
      );

      if (existingDisabled) {
        // Re-enable the existing disabled action
        (existingDisabled as OrganizationActionEntityExtendedClient).Enable();
        const saveResult = await existingDisabled.Save();
        if (!saveResult) {
          throw new Error('Failed to re-enable organization action');
        }
        this.showSuccess(`Re-enabled "${card.izzyAction.Name}"`);
      } else {
        // Create a new OrganizationAction
        const md = new Metadata();
        const newAction = await md.GetEntityObject<IzzyOrganizationActionEntity>('Organization Actions');

        newAction.OrganizationID = this.selectedOrganization.ID;
        newAction.ActionID = card.izzyAction.ActionID;
        newAction.Status = 'Active';
        newAction.Sequence = this.orgActions.length;

        const saveResult = await newAction.Save();
        if (!saveResult) {
          throw new Error('Failed to save organization action');
        }
        this.showSuccess(`Added "${card.izzyAction.Name}" to organization`);
      }

      // Mark as configured in the gallery
      card.isConfigured = true;

      // Refresh data
      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadOrganizationActions();
    } catch (error) {
      console.error('Error adding action from gallery:', error);
      this.errorMessage = 'Failed to add action. Please try again.';
    } finally {
      this.isAddingAction = false;
    }
  }

  // ========== Channel Overrides ==========

  /**
   * Toggle the channel overrides section visibility
   */
  protected toggleChannelOverrides(display: OrganizationActionDisplay): void {
    display.channelOverridesExpanded = !display.channelOverridesExpanded;
    // Reset editing state when collapsing
    if (!display.channelOverridesExpanded) {
      display.editingChannelId = null;
    }
  }

  /**
   * Check if we should show channel overrides section for this action.
   * Show if:
   * - Org has 1+ channels (for overrides to be meaningful), OR
   * - Action already has channel overrides (always show existing overrides)
   */
  protected shouldShowChannelOverrides(display: OrganizationActionDisplay): boolean {
    // Always show if there are existing overrides
    if (display.channelOverrides.length > 0) {
      return true;
    }
    // Show if org has at least 1 channel to configure
    return this.orgChannels.length >= 1;
  }

  /**
   * Get channels that don't have an override for this action
   */
  protected getChannelsWithoutOverride(display: OrganizationActionDisplay): ChannelEntityExtended[] {
    const overriddenChannelIds = new Set(display.channelOverrides.map(co => co.channel.ID));
    return this.orgChannels.filter(c => !overriddenChannelIds.has(c.ID));
  }

  /**
   * Start editing a channel override (new or existing)
   */
  protected startChannelOverrideEdit(display: OrganizationActionDisplay, channelId: string): void {
    // Cancel any org-level editing
    this.cancelAllEdits();

    display.editingChannelId = channelId;

    // Check if this channel already has an override
    const existingOverride = display.channelOverrides.find(co => co.channel.ID === channelId);

    if (existingOverride && existingOverride.channelAction) {
      // Edit existing override
      display.channelOverrideEditForm = {
        status: existingOverride.status === 'Active' ? 'Active' : 'Disabled',
        credentialId: existingOverride.credentialId,
        agentScopeMode: existingOverride.agentScopeMode === 'specific' ? 'specific' : 'global',
        selectedAgentIds: [...existingOverride.selectedAgentIds]
      };
    } else {
      // New override - inherit from org level
      const orgConfig = this.parseAgentScopeFromConfig(display.orgAction.Configuration);
      display.channelOverrideEditForm = {
        status: 'Active',
        credentialId: display.orgAction.CredentialID,
        agentScopeMode: orgConfig.mode,
        selectedAgentIds: [...orgConfig.agentIds]
      };
    }
  }

  /**
   * Cancel channel override editing
   */
  protected cancelChannelOverrideEdit(display: OrganizationActionDisplay): void {
    display.editingChannelId = null;
    display.channelOverrideEditForm = this.getEmptyChannelOverrideForm();
  }

  /**
   * Save a channel override (create or update)
   */
  protected async saveChannelOverride(display: OrganizationActionDisplay): Promise<void> {
    if (!display.editingChannelId) return;

    this.isSaving = true;
    this.errorMessage = '';

    try {
      const channelId = display.editingChannelId;
      const existingOverride = display.channelOverrides.find(co => co.channel.ID === channelId);

      // Build configuration JSON
      const configuration = this.buildConfigurationFromChannelOverrideForm(display.channelOverrideEditForm);

      if (existingOverride && existingOverride.channelAction) {
        // Update existing ChannelAction
        existingOverride.channelAction.Status = display.channelOverrideEditForm.status;
        existingOverride.channelAction.CredentialID = display.channelOverrideEditForm.credentialId;
        existingOverride.channelAction.Configuration = configuration;

        const saveResult = await existingOverride.channelAction.Save();
        if (!saveResult) {
          const errorDetail = existingOverride.channelAction.LatestResult?.Message || 'Unknown error';
          throw new Error(`Failed to update channel override: ${errorDetail}`);
        }
        this.showSuccess('Channel override updated');
      } else {
        // Create new ChannelAction
        const md = new Metadata();
        const newChannelAction = await md.GetEntityObject<IzzyChannelActionEntity>('Channel Actions');

        newChannelAction.ChannelID = channelId;
        newChannelAction.ActionID = display.orgAction.ActionID;
        newChannelAction.Status = display.channelOverrideEditForm.status;
        newChannelAction.CredentialID = display.channelOverrideEditForm.credentialId;
        newChannelAction.Configuration = configuration;
        newChannelAction.Sequence = 0;

        const saveResult = await newChannelAction.Save();
        if (!saveResult) {
          const errorDetail = newChannelAction.LatestResult?.Message || 'Unknown error';
          throw new Error(`Failed to create channel override: ${errorDetail}`);
        }
        this.showSuccess('Channel override created');
      }

      // Refresh data
      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadOrganizationActions();

      // Re-expand the section after refresh
      const refreshedDisplay = this.orgActions.find(oa => oa.orgAction.ID === display.orgAction.ID);
      if (refreshedDisplay) {
        refreshedDisplay.channelOverridesExpanded = true;
      }
    } catch (error) {
      console.error('Error saving channel override:', error);
      this.errorMessage = 'Failed to save channel override. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Remove a channel override (delete the ChannelAction)
   */
  protected confirmRemoveChannelOverride(display: OrganizationActionDisplay, override: ChannelOverride): void {
    this.showConfirmDialog(
      'Remove Channel Override',
      `Are you sure you want to remove the override for "${override.channel.Name}"? The channel will inherit the organization-level settings.`,
      async () => {
        await this.removeChannelOverride(display, override);
      }
    );
  }

  private async removeChannelOverride(display: OrganizationActionDisplay, override: ChannelOverride): Promise<void> {
    if (!override.channelAction) return;

    this.isSaving = true;
    this.errorMessage = '';

    try {
      const deleteResult = await override.channelAction.Delete();
      if (!deleteResult) {
        const errorDetail = override.channelAction.LatestResult?.Message || 'Unknown error';
        throw new Error(`Failed to delete channel override: ${errorDetail}`);
      }

      // Refresh data
      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadOrganizationActions();

      // Re-expand the section after refresh
      const refreshedDisplay = this.orgActions.find(oa => oa.orgAction.ID === display.orgAction.ID);
      if (refreshedDisplay) {
        refreshedDisplay.channelOverridesExpanded = true;
      }

      this.showSuccess('Channel override removed');
    } catch (error) {
      console.error('Error removing channel override:', error);
      this.errorMessage = 'Failed to remove channel override. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Build configuration JSON from channel override form
   */
  private buildConfigurationFromChannelOverrideForm(form: ChannelOverrideEditForm): string | null {
    const config: ChannelActionConfiguration = {};

    if (form.agentScopeMode === 'specific' && form.selectedAgentIds.length > 0) {
      config.agentScope = {
        mode: 'specific',
        agentIds: form.selectedAgentIds
      };
    }

    if (Object.keys(config).length === 0) {
      return null;
    }
    return JSON.stringify(config);
  }

  /**
   * Toggle agent selection in channel override form
   */
  protected toggleChannelOverrideAgentSelection(display: OrganizationActionDisplay, agentId: string): void {
    const form = display.channelOverrideEditForm;
    const index = form.selectedAgentIds.indexOf(agentId);
    if (index === -1) {
      form.selectedAgentIds.push(agentId);
    } else {
      form.selectedAgentIds.splice(index, 1);
    }
  }

  /**
   * Check if an agent is selected in the channel override form
   */
  protected isChannelOverrideAgentSelected(display: OrganizationActionDisplay, agentId: string): boolean {
    return display.channelOverrideEditForm.selectedAgentIds.includes(agentId);
  }

  /**
   * Get display text for channel override status
   */
  protected getChannelOverrideStatusText(override: ChannelOverride): string {
    if (override.status === 'Disabled') {
      return 'Disabled for this channel';
    }
    return 'Active';
  }

  /**
   * Get CSS class for channel override status
   */
  protected getChannelOverrideStatusClass(override: ChannelOverride): string {
    return override.status === 'Active' ? 'status-active' : 'status-disabled';
  }

  /**
   * Get channel name by ID
   */
  protected getChannelName(channelId: string | null): string {
    if (!channelId) return 'Unknown';
    const channel = this.orgChannels.find(c => c.ID === channelId);
    return channel?.Name || 'Unknown';
  }

  /**
   * Check if we're editing a NEW channel override (not an existing one)
   */
  protected isEditingNewChannelOverride(display: OrganizationActionDisplay): boolean {
    if (!display.editingChannelId) {
      return false;
    }
    // Check if the channel being edited already has an override
    const existingOverride = display.channelOverrides.find(co => co.channel.ID === display.editingChannelId);
    return !existingOverride;
  }

  // ========== Confirm Dialog ==========

  private showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  // ========== State Management ==========

  private emitStateChange(): void {
    this.stateChange.emit({
      selectedOrganizationId: this.selectedOrganization?.ID
    });
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  openOrgActionRecord(display: OrganizationActionDisplay): void {
    this.openRecord.emit({
      EntityName: 'Organization Actions',
      RecordPKey: CompositeKey.FromID(display.orgAction.ID)
    });
  }
}

export function LoadOrganizationActionsComponent(): void {
}
